import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import SmartImage from "../../components/SmartImage";
import { enquireHref } from "../../lib/format";

/**
 * One card per product variant parsed out of the migrated copy
 * (e.g. "PRE RUNNER BAR" / "FULL GUARD BULLBAR").
 * Photo on the left, feature checklist on the right, its own enquiry CTA.
 */
export default function VariantCards({ variants = [], images = [], page, parentLabel }) {
  if (!variants.length) return null;

  return (
    <div
      className={`grid gap-5 ${variants.length > 1 ? "lg:grid-cols-2" : ""}`}
      data-testid="variant-cards"
    >
      {variants.map((v, i) => (
        <article
          key={v.name}
          className="card-nq group flex flex-col overflow-hidden sm:flex-row"
          data-testid="variant-card"
        >
          <div className="relative aspect-[16/10] w-full shrink-0 sm:aspect-auto sm:min-h-[280px] sm:w-[45%]">
            <SmartImage
              src={images[i % Math.max(1, images.length)]}
              alt={v.name}
              fill
              fit="cover"
              fallbackLabel={v.name}
              className="transition-transform duration-500 group-hover:scale-[1.05]"
            />
          </div>

          <div className="flex flex-1 flex-col p-5">
            <h3 className="text-lg font-semibold text-[color:var(--nq-sand)] md:text-xl">{v.name}</h3>

            {v.features.length > 0 && (
              <ul className="mt-3 space-y-1.5">
                {v.features.map((f, fi) => (
                  <li key={fi} className="flex gap-2 text-[13px] leading-snug">
                    <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 green-text" />
                    <span className="text-[color:var(--nq-stone)]">{f}</span>
                  </li>
                ))}
              </ul>
            )}

            {v.fitment.length > 0 && (
              <div className="mt-4">
                <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[color:var(--nq-stone)]">
                  Listed for
                </p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {v.fitment.map((f) => (
                    <span
                      key={f.label}
                      className="rounded-full border px-2.5 py-1 text-[11px] font-medium text-[color:var(--nq-sand)]"
                      style={{ borderColor: "rgba(198,162,74,0.3)", background: "rgba(198,162,74,0.07)" }}
                      data-testid="variant-fitment-chip"
                    >
                      {f.label}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-auto pt-5">
              <Link
                to={enquireHref({
                  type: "page",
                  id: page.slug,
                  title: `${v.name} - ${page.title}`,
                  image: images[i % Math.max(1, images.length)],
                  category: parentLabel,
                })}
                className="btn-outline-nq w-full px-4 py-2 text-xs"
                data-testid="variant-enquire-cta"
              >
                Ask about {v.name} <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </div>
        </article>
      ))}
    </div>
  );
}
